import { Box, ColorOptions, Icon, Loader, ModalDialog, Spacer, Text } from '@jfs/ui-new'
import { defaultColors } from '@jfs/ui-new/src/theme/DefaultTheme'
import { getErrorMessage, getLoadingState } from '@jfs/utils'
import { useNavigation, useRoute } from '@react-navigation/native'
import * as React from 'react'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import ActionsMenu from '../../comps/ActionMenu/ActionsMenu'
import EmptyView from '../../comps/EmptyView'
import { ModBox } from '../../comps/ModifiedBox/ModifiedBox'
import { NewTable } from '../../comps/NewTable'
import { TimeOut, Toast, ToastPosition, ToastType } from '../../comps/Toast'
import { MAX_TRANSACTIONS } from '../../data/static/data'
import { headerTitle, staticStrings, transactionScreenStrings } from '../../data/static/strings'
import { RootStore, useLazyApiData, useStores } from '../../data/store'
import { Routes } from '../../nav/routes'
import { shouldShowDateForSearch } from '../../util/common'
import { Colors, getCustomColumnWidth, getWidth, isMobile, tableConfig, useWindowDimensions } from '../../util/util'
import TransactionDetails from '../TransactionDetails'
import { CreateSplitSettlement } from './CreateSplitSettlement'
import styles from './styles'
import { TransactionsType } from './types'

const Transactions: FC = () => {
  const themes = styles(0)
  const {
    mid,
    updateSelectedTxn,
    selectedDateInfo,
    updateSelectedDate,
    getTrxn,
    getTransactionsColumn,
    exportFiles,
    onTransactionRetry,
    getTransactionScreenData,
    fetchTransactionDetailsOfPath,
    isFieldsNotEmpty,
    showSearchResult,
    searchString,
    resetFilters,
    setDownloadReportType,
    toastMessage,
    toastResetMessage,
    pagination,
    canExportTransationsReport,
    canViewTransactionDetails,
    getSplitActionMenuData,
    isSplitSettlementsEnabled,
    applyFieldsSetting,
    searchFilterItem,
  } = useStores((store) => ({
    mid: store.user.selectedMid,
    pageNumber: store.transactions.pagination.number,
    updateSelectedTxn: store.transactions.updateSelectedTxn,
    selectedDateInfo: store.transactions.selectedDateInfo,
    updateSelectedDate: store.transactions.updateSelectedDateInfo,
    getTrxn: store.transactions.getTransactionsV3,
    getTransactionsColumn: store.transactions.getTransactionsColumnV3,
    exportFiles: store.transactions.exportFilesTxnV3,
    onTransactionRetry: store.onTransactionRetry,
    getTransactionScreenData: store.getTransactionScreenData,
    fetchTransactionDetailsOfPath: store.fetchTransactionDetailsOfPath,
    isFieldsNotEmpty: store.transactions.isFieldsNotEmpty,
    showSearchResult: store.settings.metaData.showSearchResult,
    searchString: store.settings.metaData.searchString,
    resetFilters: store.transactions.resetFilters,
    setDownloadReportType: store.settings.metaData.setDownloadReportType,
    toastMessage: store?.reports?.getMessage(),
    toastResetMessage: store?.reports?.resetMessage,
    pagination: store.transactions.pagination,
    canExportTransationsReport: store.userManagement.canExportTransationsReport(),
    canViewTransactionDetails: store.userManagement.canViewTransactionDetails(),
    getSplitActionMenuData: store.getSplitActionMenuData,
    isSplitSettlementsEnabled: store.settings.metaData.isSplitSettlementsEnabled,
    applyFieldsSetting: store.transactions.applyFieldSettings,
    searchFilterItem: store.settings.metaData.searchFilterItem,
  }))
  const viewPortWidth = useWindowDimensions()
  console.log('viewPortWidth', viewPortWidth)
  const smallDimension = viewPortWidth < 416
  const isSmallDevice = isMobile(!smallDimension)

  const abortControllerRef = useRef<AbortController>()
  const navigate = useNavigation().navigate
  const path = useRoute().name
  const [showTransactionDetails, setTransactionDetails] = useState(false)
  const [trxId, setTrxId] = useState(transactionScreenStrings.empty)
  const [essStatus, setEssStatus] = useState(transactionScreenStrings.empty)
  const [trxDate, setTrxDate] = useState('')
  const listData = getTransactionScreenData(path, isSmallDevice)
  const [fieldSettingVisible, setFieldSettingVisible] = useState(false)
  const [filterVisible, setFilterVisible] = useState(false)
  const [showMax100Info, setMax100Info] = useState(true)
  const [createSettlementVisible, setCreateSettlementVisible] = useState(false)
  const [selectedTxnForSplitOrRefund, setSelectedTxnForSplitOrRefund] = useState()

  const [fetchTxnColumns, txnColumnHandler] = useLazyApiData(getTransactionsColumn)
  const [fetchTxn, fetchTxnHandler] = useLazyApiData(getTrxn)
  const [exportFile, expoFileHandler] = useLazyApiData(exportFiles)

  const apis = [txnColumnHandler, fetchTxnHandler]
  const { isTransactionApiCalled } = (useRoute().params ?? {}) as any

  useEffect(() => {
    if (txnColumnHandler?.state === 'fulfilled') {
      const savedFieldSettingData = window.localStorage.getItem('transaction_field_setting')
      if (savedFieldSettingData && savedFieldSettingData.length > 0) {
        applyFieldsSetting(JSON.parse(savedFieldSettingData))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txnColumnHandler?.state])

  const navigateTo = () => {
    const getUrl: any = {
      [TransactionsType.transactions]: Routes.TRX_INITIATEREFUND,
      [TransactionsType.refunds]: Routes.REFUND_INITIATEREFUND,
    }
    navigate(getUrl[path] || Routes.HOME)
  }

  const fetchDetailOfPath = () => {
    path && setTransactionDetails(false)
    abortControllerRef?.current?.abort()
    abortControllerRef.current = new AbortController()
    fetchTransactionDetailsOfPath(
      fetchTxnColumns,
      fetchTxn,
      false,
      selectedDateInfo,
      pagination,
      true,
      isSmallDevice,
      abortControllerRef?.current,
    )
  }

  const fetchDetailsOfPath = useCallback(fetchDetailOfPath, [fetchTxn, fetchTxnColumns])

  useEffect(() => {
    resetFilters()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path])

  useEffect(() => {
    if (!isFieldsNotEmpty && mid) {
      fetchTxnColumns()
    } else if (mid && !isTransactionApiCalled) {
      fetchDetailsOfPath()
    }
  }, [path, mid, isFieldsNotEmpty, isTransactionApiCalled])

  const handleRetry = (): void => {
    abortControllerRef?.current?.abort()
    abortControllerRef.current = new AbortController()
    onTransactionRetry(fetchTxn, fetchTxnColumns, selectedDateInfo, isSmallDevice, abortControllerRef?.current)
  }

  const onPress = (item: any) => {
    if (canViewTransactionDetails) {
      setTrxId(item.transactionId)
      setTrxDate(item.transactionDateTime)
      setTransactionDetails(true)
      setEssStatus(item.essStatus)
    } else {
      RootStore?.response?.setCustomToast({
        message: staticStrings.notAuthorized,
        color: defaultColors.RED,
        position: ToastPosition.BOTTOM,
        toastType: ToastType.NEGATIVE,
        minWidthOfLeftBox: 200,
      })
    }
  }

  const closeTrxDetailsView = () => {
    setTransactionDetails(false)
    setTrxId('')
    setTrxDate('')
    setEssStatus('')
  }

  const initiateRefund = (id: string) => {
    updateSelectedTxn(id)
    navigateTo()
    closeTrxDetailsView()
  }

  const renderTransactionDetails = (): React.ReactElement => {
    return (
      <TransactionDetails
        transactionId={trxId}
        transactionDate={trxDate}
        visible={showTransactionDetails}
        closeTrxDetailsView={closeTrxDetailsView}
        initiateRefund={initiateRefund}
        path={path}
        essStatus={essStatus}
      />
    )
  }

  const loadingState = getLoadingState(apis)
  const errorState = getErrorMessage(apis)
  const emptyState = listData.length > 0 && !loadingState

  const getTitle = (): string => {
    return headerTitle[tableConfig(path).status]
  }

  const getEmptyMessage = (): string => {
    return listData.length === 0 ? transactionScreenStrings.noRecords : transactionScreenStrings.empty
  }

  const closeModal = () => {
    setTransactionDetails(false)
    setTrxId('')
    setEssStatus('')
  }

  // const onItemAction = () => {}
  const pageTitle = getTitle()

  const scrollViewStyle = () => {
    if (listData.length === 0) {
      return themes[path as TransactionsType].emptyContainer
    }
    return themes[path as TransactionsType].tableContainer(loadingState)
  }
  const scrollStyle = scrollViewStyle()

  const handleToastAction = () => {
    setDownloadReportType('Transaction')
    toastResetMessage()
    navigate(Routes.DOWNLOADS)
  }
  const handleToastDismiss = () => {
    toastResetMessage()
  }

  const onCreateSplitClick = (item) => {
    setSelectedTxnForSplitOrRefund(item)
    setCreateSettlementVisible(true)
  }

  const onRefundClick = (item) => {
    console.log(item)
  }
  return (
    <Box f={1} overflowY={fieldSettingVisible || filterVisible ? 'auto' : 'hidden'}>
      <Box {...themes.maincontainer}>
        <Toast
          message={toastMessage}
          hide={toastMessage.message === ''}
          position={ToastPosition.TOP_POSITIVE_ACTION_VIEW}
          type={ToastType.POSITIVE_ACTION_VIEW}
          onDismiss={handleToastDismiss}
          onAction={handleToastAction}
          actionLabel={'Go To Downloads'}
          timeOut={TimeOut.FIVE_SECONDS}
        />
        <ModalDialog
          children={renderTransactionDetails()}
          show={showTransactionDetails}
          type={'Right'}
          onDismiss={closeModal}
        />
        <Box {...themes[path as TransactionsType].container} flex={emptyState ? undefined : 1}>
          <Box {...themes.stickyHeader}>
            <Box flexDirection={'row'} alignContent={'center'}>
              <Text testID={'pageTitle'} color={'BLACK_21'} fontSize={20} fontFamily={'jiotype-medium'} lineHeight={20}>
                {pageTitle}
              </Text>
              <Spacer direction={'horizontal'} size={16} />
              <Text
                hide={searchString?.length === 0}
                color={'BLACK_75'}
                fontSize={14}
                fontFamily={'jiotype-medium'}
                lineHeight={20}>
                {`Showing results for "${searchString}"`}
              </Text>
            </Box>
            <ActionsMenu
              path={path}
              handleExport={exportFile}
              fetchDetailOfPath={fetchDetailOfPath}
              fetchTxnColumns={fetchTxnColumns}
              fieldSettingVisible={fieldSettingVisible}
              setFieldSettingVisible={setFieldSettingVisible}
              filterVisible={filterVisible}
              setFilterVisible={setFilterVisible}
              selectedDateInfo={selectedDateInfo}
              updateSelectedDate={updateSelectedDate}
              hideExportButton={!canExportTransationsReport || searchString?.length > 0}
              hideDateSelector={searchString?.length > 0 && !shouldShowDateForSearch(searchFilterItem)}
              maxRange={31}
            />
          </Box>
          <ModBox
            show={pagination?.totalElements === MAX_TRANSACTIONS && showMax100Info && !fieldSettingVisible}
            flexDirection={'row'}
            align={'center'}
            maxHeight={24}
            borderWidth={1}
            ph={8}
            pv={2}
            borderRadius={4}
            borderColor={Colors.INFO_BORDER as ColorOptions}
            bg={Colors.INFO_BG as ColorOptions}>
            <Box flexGrow={1} flexDirection={'row'} align={'center'} f={1}>
              <Icon id={'INFO'} color={'BRAND_PRIMARY_ORANGE'} size={16} />
              <Spacer direction={'horizontal'} size={8} />
              <Text
                title={`Showing only upto ${MAX_TRANSACTIONS} records here. To check more records, you can 'Export' the records. The reports will be downloaded in the 'Download' section of the menu.`}
                fontSize={12}
                fontFamily={'jiotype-medium'}
                color={'BLACK_21'}
                numberOfLines={1}
              />
            </Box>
            <Box underlayColor={'TRANSPARENT'} testID="close" onPress={() => setMax100Info(false)}>
              <Icon id={'CLOSE'} color={'BLACK_21'} size={14} />
            </Box>
          </ModBox>
          <EmptyView
            onRetry={handleRetry}
            isLoading={loadingState}
            error={errorState}
            emptyMessage={getEmptyMessage()}
            loadingMessage={transactionScreenStrings.loading}
            hide={emptyState}
            loaderColor={'BLUE'}
          />
          <ModBox show={expoFileHandler.state === 'pending'}>
            <Loader color="BLUE" />
          </ModBox>
          <Spacer size={5} direction="vertical" />
          <Box {...scrollStyle} nativeID={'scroll-horizontal-Table'} f={1}>
            <NewTable
              data={listData.slice(1)}
              rowHeight={48}
              headerHeight={64}
              onRowClick={onPress}
              sortBy={'transactionId'}
              customWidthFunc={getCustomColumnWidth}
              tableWidth={getWidth(
                viewPortWidth > 1200 ? viewPortWidth - 298 : viewPortWidth + 290,
                listData && listData[0],
              )}
              hide={filterVisible || fieldSettingVisible || !(!loadingState && !errorState && !getEmptyMessage())}
              headerRowKeys={listData && listData[0]}
              freezeColumnWidth={270}
              isSplitSettlementsEnabled={isSplitSettlementsEnabled}
              splitActionMenuData={(rowData) =>
                getSplitActionMenuData(onCreateSplitClick, () => initiateRefund(rowData?.transactionId), rowData, path)
              }
            />
          </Box>
          {createSettlementVisible && (
            <CreateSplitSettlement
              visible={createSettlementVisible}
              data={selectedTxnForSplitOrRefund}
              close={() => setCreateSettlementVisible(false)}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default Transactions
